import * as R from 'ramda';
import React from 'react';
import {SocialAuthIntegration} from '@practicegenius/domain/src/data/integrations';
import {
  Button,
  ButtonWidth,
} from '@practicegenius/pg-ui/src/base/Material/components/Button';
import Box from '@mui/material/Box';
import AppleIcon from '@mui/icons-material/Apple';
import {inMobileApp} from '../utils/MobileApp';
import {
  SocialOauthAppSpecificProps,
  useSocialOauth,
} from '../hooks/useSocialAuth';
import {EmailIcon} from '../icons/EmailIcon';
import {ColoredGoogleLogo} from '../icons/social/ColoredGoogleLogo';
import {FacebookCircleLogo} from '../icons/social/FacebookCircleLogo';
import {SvgIconComponent} from '../icons/types';

export const socialPlatformLabels: {
  [k in SocialAuthIntegration]: string;
} = {
  facebook: 'Facebook',
  google: 'Google',
  apple: 'Apple',
};

const socialButtonIcons: Record<
  SocialAuthIntegration | 'email',
  SvgIconComponent
> = {
  apple: AppleIcon,
  facebook: props => <FacebookCircleLogo useBrandColor {...props} />,
  google: ColoredGoogleLogo,
  email: EmailIcon,
};

export type SocialLoginButtonsAppSpecificProps = SocialOauthAppSpecificProps & {
  buildSocialProviderLoginLink: (props: {
    provider: SocialAuthIntegration;
  }) => string;
  onAuthSuccess: (
    platform: SocialAuthIntegration,
    payload: {providerToken: string},
  ) => unknown;
};

export type SocialLoginButtonsProps = {
  appProps: SocialLoginButtonsAppSpecificProps;
  platforms: ReadonlyArray<SocialAuthIntegration>;
};

const LoginButton = ({
  appProps,
  platform,
  lastUsed,
}: {
  appProps: SocialLoginButtonsProps['appProps'];
  platform: SocialAuthIntegration;
  lastUsed: boolean;
}) => {
  const {startAuth} = useSocialOauth({
    appProps,
    enabled: inMobileApp,
    platform,
    authPopupUrl: `/login/social_provider/${platform}`,
    onAuthSuccess: payload => appProps.onAuthSuccess(platform, payload),
  });

  const ButtonIcon = socialButtonIcons[platform];
  return (
    <Button
      key={platform}
      // This page is has the same route and param structure in kohana and next.js
      href={appProps.buildSocialProviderLoginLink({provider: platform})}
      onClick={e => {
        if (inMobileApp) {
          e.preventDefault();
          startAuth();
        }
      }}
      width={ButtonWidth.FULL_WIDTH}
      data-external
      sx={{
        height: '55px',
        backgroundColor: 'common.white',
        color: 'text.secondary',
        border: '1px solid rgba(0,0,0,0.60)',
        borderWidth: lastUsed ? '2px' : undefined,
        '&:hover,&:active,&:focus': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        justifyContent: 'flex-start',
      }}
      startIcon={
        <Box
          sx={{
            borderTopLeftRadius: '5px',
            borderBottomLeftRadius: '5px',
            width: '75px',
            height: '55px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            // Pull icons farther left to more closely match old style
            marginLeft: '-15px',
          }}
        >
          <ButtonIcon />
        </Box>
      }
    >
      Continue With {socialPlatformLabels[platform]}
    </Button>
  );
};

export function SocialLoginButtons(props: SocialLoginButtonsProps) {
  const lastUsedSocialProvider =
    props.appProps.lastUsedSocialProviderManager.get();

  // The last used platform always shows at the top of the list
  const sortedPlatforms = lastUsedSocialProvider
    ? [
        lastUsedSocialProvider,
        ...R.reject(R.equals(lastUsedSocialProvider), props.platforms),
      ]
    : props.platforms;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
          maxWidth: '300px',
        }}
      >
        {sortedPlatforms.map(platform => (
          <LoginButton
            appProps={props.appProps}
            lastUsed={platform === lastUsedSocialProvider}
            key={platform}
            platform={platform}
          />
        ))}
      </Box>
    </Box>
  );
}
